/**
 * COReviewQueue - TASK-374: CO Review System
 *
 * Table of submitted KYC profiles awaiting compliance officer review.
 * Supports filtering by status, sorting by date, and one-click review assignment.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardCheck,
  AlertTriangle,
  Search,
  ArrowUpDown,
  Loader2,
  Inbox,
  Play,
} from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../../../utils/logger';
import { getReviewQueue, assignReview } from '../../../services/reviewService';
import { formatDate } from '../../../utils/formatters';
import type { KYCReview } from '../../../types/complianceWorkflow';

export type StatusFilter = 'all' | 'submitted' | 'in_review' | 'changes_requested' | 'rejected';
type SortDirection = 'asc' | 'desc';

// Optional `status` seeds the initial filter when summoned onto the canvas (TASK-495);
// the on-tile status buttons below still let the RM change it locally afterwards.
//
// TASK-733: `onSelectReview` is the HOST's exit. A canvas host passes it to open the
// review as a workspace tile (the browser never navigates away); without it the queue
// keeps its original route navigation — the full-page flow is unchanged.
const COReviewQueue: React.FC<{
  status?: StatusFilter;
  onSelectReview?: (review: KYCReview) => void;
}> = ({ status, onSelectReview }) => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState<KYCReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(status ?? 'all');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [assigningId, setAssigningId] = useState<string | null>(null);

  const fetchQueue = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = statusFilter !== 'all' ? { status: statusFilter } : undefined;
      const data = await getReviewQueue(params);
      setReviews(data.items);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load review queue';
      setError(message);
      logger.error('Failed to fetch review queue:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  // TASK-733: a decision submitted in a canvas workspace tile refreshes this queue in
  // place — the two are separate tiles, so the workspace broadcasts and the queue listens.
  useEffect(() => {
    const onDecided = () => fetchQueue();
    window.addEventListener('kielis:review-decided', onDecided);
    return () => window.removeEventListener('kielis:review-decided', onDecided);
  }, [fetchQueue]);

  const openReview = (review: KYCReview) => {
    if (onSelectReview) onSelectReview(review);
    else navigate(`/compliance/review/${review.id}`);
  };

  const handleStartReview = async (review: KYCReview) => {
    try {
      setAssigningId(review.id);
      const assigned = await assignReview(review.kyc_profile_id || review.id);
      toast.success('Review assigned successfully');
      openReview(assigned);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to assign review';
      toast.error(message);
      logger.error('Failed to assign review:', err);
    } finally {
      setAssigningId(null);
    }
  };

  const sortedReviews = useMemo(() => {
    const sorted = [...reviews].sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return sortDirection === 'desc' ? dateB - dateA : dateA - dateB;
    });
    return sorted;
  }, [reviews, sortDirection]);

  const toggleSort = () => {
    setSortDirection((prev) => (prev === 'desc' ? 'asc' : 'desc'));
  };

  const getRiskBadge = (review: KYCReview) => {
    // Risk level derived from metadata if available, fallback to 'medium'
    const metadata = review as unknown as Record<string, unknown>;
    const risk = (metadata.risk_level as string) || 'medium';

    const config: Record<string, { bg: string; text: string; label: string }> = {
      low: { bg: 'bg-green-100', text: 'text-green-800', label: 'Low' },
      medium: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Medium' },
      high: { bg: 'bg-red-100', text: 'text-red-800', label: 'High' },
      critical: { bg: 'bg-red-200', text: 'text-red-900', label: 'Critical' },
    };

    const c = config[risk] || config.medium;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
        {c.label}
      </span>
    );
  };

  const getStatusBadge = (review: KYCReview) => {
    const status = (review as unknown as Record<string, unknown>).status as string | undefined;

    if (status === 'changes_requested') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          Changes Requested
        </span>
      );
    }

    if (status === 'rejected') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          Rejected
        </span>
      );
    }

    const hasDecision = !!review.decision;
    const isAssigned = !!review.reviewer_user_id;

    if (hasDecision) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Decided
        </span>
      );
    }

    if (isAssigned) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          In Review
        </span>
      );
    }

    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        Submitted
      </span>
    );
  };

  const filterButtons: { value: StatusFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'in_review', label: 'In Review' },
    { value: 'changes_requested', label: 'Changes Requested' },
    { value: 'rejected', label: 'Rejected' },
  ];

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-3" />
        <p className="text-sm text-gray-500">Loading review queue...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <AlertTriangle className="w-8 h-8 text-red-500 mb-3" />
        <p className="text-sm text-red-600 mb-2">{error}</p>
        <button
          onClick={fetchQueue}
          className="text-sm text-blue-600 hover:text-blue-800 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-blue-600" />
            KYC Review Queue
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {reviews.length} review{reviews.length !== 1 ? 's' : ''} pending
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2">
        {filterButtons.map((btn) => (
          <button
            key={btn.value}
            onClick={() => setStatusFilter(btn.value)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              statusFilter === btn.value
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {btn.label}
          </button>
        ))}
      </div>

      {/* Table */}
      {sortedReviews.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-white rounded-lg border border-gray-200">
          <Inbox className="w-10 h-10 text-gray-300 mb-3" />
          <p className="text-sm text-gray-500">No reviews in queue</p>
          <p className="text-xs text-gray-400 mt-1">
            New KYC submissions will appear here
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client Name
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    onClick={toggleSort}
                    className="flex items-center gap-1 hover:text-gray-700"
                  >
                    Submitted Date
                    <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Risk Level
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reviewer
                </th>
                <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sortedReviews.map((review) => (
                <tr key={review.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-gray-900">
                      {review.client_name || 'Unknown Client'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {getStatusBadge(review)}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600">
                      {formatDate(review.created_at)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {getRiskBadge(review)}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600">
                      {review.reviewer_name || (review.reviewer_user_id ? 'Assigned' : '--')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!review.decision && (
                      <button
                        onClick={() => handleStartReview(review)}
                        disabled={assigningId === review.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {assigningId === review.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Play className="w-3.5 h-3.5" />
                        )}
                        {review.reviewer_user_id ? 'Continue Review' : 'Start Review'}
                      </button>
                    )}
                    {review.decision && (
                      <button
                        onClick={() => openReview(review)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        <Search className="w-3.5 h-3.5" />
                        View
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default COReviewQueue;
